/**
 * Schemas Zod para dados do contrato (preenchidos pelo cliente após o aceite).
 *
 * Fluxo:
 *   1. Cliente aceita proposta (POST /aceitar) → token fill-contract emitido
 *   2. Cliente preenche contatos operacionais (POST /contatos-contrato)
 *   3. Cliente preenche dados do contrato (POST /dados-contrato)  ← este schema
 *   4. Backend gera contrato → envia pra assinatura digital
 */

import { z } from 'zod';

import { enderecoSchema } from './cliente.js';
import {
  cnpjSchema,
  cpfSchema,
  emailSchema,
  isoTimestampSchema,
  telefoneBrSchema,
  textoCurtoSchema,
} from './common.js';

/** Responsável legal — quem assina o contrato pelo cliente */
export const responsavelLegalSchema = z.object({
  nome: textoCurtoSchema,
  cpf: cpfSchema,
  cargo: textoCurtoSchema,
  email: emailSchema,
  telefone: telefoneBrSchema,
});

/** Dia de vencimento da fatura — cliente escolhe entre 15, 20 ou 25 */
export const diaVencimentoSchema = z.union([z.literal(15), z.literal(20), z.literal(25)]);

export type DiaVencimento = z.infer<typeof diaVencimentoSchema>;

/**
 * Payload completo de DadosContrato — dados DO CLIENTE que entram no contrato.
 * Aceito via POST /api/public/propostas/{token}/dados-contrato.
 *
 * NOTA: dados bancários da Infomatic não entram no contrato — cobrança é 100%
 * via Omie (boleto/PIX gerado lá). Por isso não há campo de banco/conta/PIX.
 *
 * Aceita PF (com `cpf`) ou PJ (com `cnpj`). Pelo menos um dos dois é obrigatório.
 */
// Schema base — ZodObject puro, permite `.extend()`. Validação "pelo menos um documento"
// é aplicada no `dadosContratoSchema` exportado abaixo via `.refine()`.
const dadosContratoBaseSchema = z.object({
  /** Razão social (PJ) ou Nome completo (PF) */
  razaoSocial: textoCurtoSchema,
  /** CNPJ (PJ) — 14 dígitos */
  cnpj: cnpjSchema.optional(),
  /** CPF (PF) — 11 dígitos */
  cpf: cpfSchema.optional(),
  /** IE (só aplicável a PJ) */
  inscricaoEstadual: z.string().max(20).optional(),
  /** IM (só aplicável a PJ) */
  inscricaoMunicipal: z.string().max(20).optional(),
  endereco: enderecoSchema,
  responsavelLegal: responsavelLegalSchema,
  /** Dia de vencimento escolhido pelo cliente: 15, 20 ou 25 */
  diaVencimento: diaVencimentoSchema,
});

/** Refinement comum: pelo menos um documento (CNPJ ou CPF) deve estar presente. */
function temAlgumDocumento(d: { cnpj?: string; cpf?: string }): boolean {
  return Boolean(d.cnpj || d.cpf);
}

const REFINEMENT_DOC = {
  message: 'Informe CNPJ (PJ) ou CPF (PF)',
  path: ['cnpj' as const],
};

/** Schema de input público com refinamento. Usado no POST /dados-contrato. */
export const dadosContratoSchema = dadosContratoBaseSchema.refine(
  temAlgumDocumento,
  REFINEMENT_DOC,
);

/** Payload de input do endpoint público (sem preenchidoEm — server gera) */
export type DadosContratoInput = z.infer<typeof dadosContratoSchema>;

/** Dados persistidos (com timestamp servidor). Refinamento aplicado também. */
export const dadosContratoPersistidoSchema = dadosContratoBaseSchema.extend({
  preenchidoEm: isoTimestampSchema,
});

export type DadosContratoPersistido = z.infer<typeof dadosContratoPersistidoSchema>;
